import type { ReturnRequestDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { currencyFromISO } from "@/utils/currency";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@babackapp/ui/components/tooltip";

export interface FinancialAmountProps {
  returnRequest: ReturnRequestDto;
  charges: any[];
  totalWithoutCharges: number;
  total: number;
  /** When set, the main amount is displayed in shopCurrency with a customerCurrency equivalent in parentheses. */
  shopCurrencyDisplay?: {
    shopTotal: number;
    shopCurrency: string;
    customerTotal: number;
    customerCurrency: string;
  };
  /** When set, shows the shop-currency amount the merchant must issue so that
   *  Shopify displays the correct customer-currency amount (customerTotal ÷ rate). */
  customRateDisplay?: {
    shopTotal: number;
    shopCurrency: string;
    rate: number;
  };
}

function MultiCurrencyAmount({
  shopCurrencyDisplay,
  customRateDisplay,
}: {
  shopCurrencyDisplay: NonNullable<FinancialAmountProps["shopCurrencyDisplay"]>;
  customRateDisplay?: FinancialAmountProps["customRateDisplay"];
}) {
  const t = useTranslation();

  return (
    <>
      {customRateDisplay && (
        <Tooltip delayDuration={150}>
          <TooltipContent className="text-black bg-white border-input border w-auto p-3">
            <p className="text-xs text-gray-500 mb-2">
              {t("returnDetail.customRateTooltipRate", {
                rate: customRateDisplay.rate,
                shopCurrency: customRateDisplay.shopCurrency,
                customerCurrency: shopCurrencyDisplay.customerCurrency,
              })}
            </p>
            <div className="space-y-1.5 text-sm">
              <div className="flex items-center justify-between gap-4">
                <span className="text-gray-600">
                  {t("returnDetail.customRateTooltipIssue")}
                </span>
                <span className="font-semibold text-amber-600">
                  {customRateDisplay.shopTotal.toFixed(2)}{" "}
                  {currencyFromISO(customRateDisplay.shopCurrency)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-gray-400">
                  {t("returnDetail.customRateTooltipStandard")}
                </span>
                <span className="line-through text-gray-400">
                  {shopCurrencyDisplay.shopTotal.toFixed(2)}{" "}
                  {currencyFromISO(shopCurrencyDisplay.shopCurrency)}
                </span>
              </div>
            </div>
          </TooltipContent>
          <TooltipTrigger asChild>
            <span className="mr-2 inline-flex items-center gap-1 text-amber-600 font-medium cursor-default">
              <Info className="h-3.5 w-3.5" />
              {customRateDisplay.shopTotal.toFixed(2)}{" "}
              {currencyFromISO(customRateDisplay.shopCurrency)}
            </span>
          </TooltipTrigger>
        </Tooltip>
      )}
      <span
        className={customRateDisplay ? "line-through text-gray-400" : undefined}
      >
        {shopCurrencyDisplay.shopTotal.toFixed(2)}{" "}
        {currencyFromISO(shopCurrencyDisplay.shopCurrency)}
      </span>
      <span className="text-gray-500 ml-1">
        ({"\u2248"} {shopCurrencyDisplay.customerTotal.toFixed(2)}{" "}
        {currencyFromISO(shopCurrencyDisplay.customerCurrency)})
      </span>
    </>
  );
}

export default function FinancialAmount(props: FinancialAmountProps) {
  const t = useTranslation();

  if (props.charges.length === 0) {
    return (
      <div className="text-sm font-medium text-gray-900 flex items-center">
        {props.shopCurrencyDisplay ? (
          <MultiCurrencyAmount
            shopCurrencyDisplay={props.shopCurrencyDisplay}
            customRateDisplay={props.customRateDisplay}
          />
        ) : (
          <>
            {props.total.toFixed(2)}{" "}
            {currencyFromISO(props.returnRequest.currency.customerCurrency)}
          </>
        )}
      </div>
    );
  }

  return (
    <Tooltip delayDuration={150}>
      <TooltipContent className="text-black bg-white border-input border">
        <div className="space-y-2">
          <div className="flex">
            <div className="flex-1">{t("returns.items")}</div>
            <div>
              {props.totalWithoutCharges.toFixed(2)}{" "}
              {currencyFromISO(props.returnRequest.currency.customerCurrency)}
            </div>
          </div>
          {props.charges.map((charge, index) => (
            <div key={index} className="flex gap-1">
              <div className="flex-1">
                {charge.id == "initialShippingRefunded" &&
                  t("returnDetail.confirmModal.refundShippingCost")}
                {charge.id == "returnShippingPaid" &&
                  t("returnDetail.confirmModal.returnShippingCost")}
                {charge.id == "discountBonus" &&
                  t("returnDetail.confirmModal.bonusVoucher")}
                {charge.id == "originalDiscount" &&
                  t("returnDetail.confirmModal.voucherWhenOrdering")}
              </div>
              <div>
                {(-1 * charge.presentmentAmount).toFixed(2)}{" "}
                {currencyFromISO(props.returnRequest.currency.customerCurrency)}
              </div>
            </div>
          ))}
          <div className="flex font-medium border-t border-gray-200 py-2">
            <div className="flex-1">{t("returnDetail.totalRefunded")}</div>
            <div>
              {props.total.toFixed(2)}{" "}
              {currencyFromISO(props.returnRequest.currency.customerCurrency)}
            </div>
          </div>
          {props.returnRequest.shipping_info.carrier === "global-e" &&
            props.charges.some((c) => c.id === "returnShippingPaid") && (
              <p className="text-xs text-gray-400 mt-1">
                {t("returnDetail.globalEReturnCostHint")}
              </p>
            )}
        </div>
      </TooltipContent>
      <TooltipTrigger asChild>
        <div className="text-sm font-medium text-gray-900 flex items-center">
          <Info className="h-4 w-4 mr-1" />
          {props.shopCurrencyDisplay ? (
            <span>
              <MultiCurrencyAmount
                shopCurrencyDisplay={props.shopCurrencyDisplay}
                customRateDisplay={props.customRateDisplay}
              />
            </span>
          ) : (
            <span>
              {props.total.toFixed(2)}{" "}
              {currencyFromISO(props.returnRequest.currency.customerCurrency)}
            </span>
          )}
        </div>
      </TooltipTrigger>
    </Tooltip>
  );
}
